import { useState, useEffect } from 'react';
import CalendarSlideshow from '../components/CalendarSlideshow';
import PurchaseFormModal from '../components/PurchaseFormModal';

function Shop() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const originalPrice = 699;
  const discountedPrice = 499;
  const isSoldOut = true; // Add sold out state

  useEffect(() => {
    document.title = "Shop - 2025 Calendar Collection | Deepali Agrawal";
  }, []);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 md:py-20">
        <h1 className="text-3xl md:text-4xl font-fraunces mb-8 md:mb-12 text-center md:text-left">Shop</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Calendar Product Card */}
          <div className="bg-gradient-to-br from-[#f8f8f8] via-[#f0f0f0] to-[#e8f0f0] rounded-[24px] md:rounded-[32px] p-6 sm:p-8 md:p-12">
            <div className="mb-6 md:mb-8">
              <CalendarSlideshow showControls={true} />
            </div>
            
            <div>
              <div className="flex flex-col sm:flex-row items-center md:items-start gap-2 mb-4">
                <h2 className="text-2xl md:text-3xl font-fraunces text-center md:text-left">2025 Calendar</h2>
                {isSoldOut && (
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                    Sold Out
                  </span>
                )}
              </div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 text-center md:text-left">
                This calendar is a heartfelt journey through childhood memories and simple joys. From the warmth of a bonfire to the whimsy of spinning tops and the joy of building pillow forts, each month brings moments that rekindle forgotten happiness, refill your spirit with positivity, and inspire reflection. 
                These illustrations invites you to cherish old memories, find beauty in small things, and embrace new beginnings with warmth, hope and gratitude.
              </p>

              {/* Price Section */}
              <div className="mb-4 md:mb-6 flex flex-col items-center md:items-start">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-2xl md:text-3xl font-fraunces">₹{discountedPrice}</span>
                  <span className="text-base md:text-lg text-gray-500 line-through">₹{originalPrice}</span>
                  <span className="text-green-600 text-sm">
                    ({Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)}% off)
                  </span>
                </div>
                <span className="text-sm text-gray-600">Inclusive of delivery across India</span>
              </div>

              {/* Features */}
              <div className="mb-6 md:mb-8 bg-white/50 rounded-2xl p-4 md:p-6">
                <h3 className="text-lg font-medium mb-3 text-center md:text-left">Calendar Features:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    12 unique nostalgic illustrations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Premium quality paper
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Desktop stand included
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Size: A6
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Perfect for gifting
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <button 
                  disabled={isSoldOut}
                  onClick={() => !isSoldOut && setIsModalOpen(true)}
                  className={`w-full sm:w-auto inline-flex items-center justify-center px-6 md:px-8 py-3 rounded-full transition-colors duration-300 text-base md:text-lg
                    ${isSoldOut 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-gray-800'
                    }`}
                >
                  {isSoldOut ? 'Sold Out' : 'Purchase Now'}
                </button>
              </div>
            </div>
          </div>
          {/* Weekly Planner Product Card */}
          <div className="bg-gradient-to-br from-[#f8f8f8] via-[#f0f0f0] to-[#e8f0f0] rounded-[24px] md:rounded-[32px] p-6 sm:p-8 md:p-12">
            <div className="mb-6 md:mb-8 relative">
              <img 
                src="/images/planner-preview.jpg" 
                alt="Weekly Planner Preview" 
                className="w-full h-auto rounded-2xl bg-gray-100"
              />
              <div className="absolute top-4 right-4 bg-black text-white px-4 py-2 rounded-full text-sm font-medium">
                Coming Soon
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl md:text-3xl font-fraunces mb-4 text-center md:text-left">Weekly Planner</h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 text-center md:text-left">
                A thoughtfully designed weekly planner to help you organize your days with style. Perfect for setting intentions, tracking goals, and making every week count. Pre-order now and be among the first to experience this beautiful planning companion.
              </p>

              {/* Price Section */}
              <div className="mb-4 md:mb-6 flex flex-col items-center md:items-start">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-2xl md:text-3xl font-fraunces">₹699</span>
                  <span className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-full">Pre-order</span>
                </div>
                <span className="text-sm text-gray-600">Inclusive of delivery across India</span>
              </div>

              {/* Features */}
              <div className="mb-6 md:mb-8 bg-white/50 rounded-2xl p-4 md:p-6">
                <h3 className="text-lg font-medium mb-3 text-center md:text-left">Planner Features:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    60 sheets of premium quality paper
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Hardcover with elegant finish
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    A5 size (148 × 210 mm)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Weekly layout with notes section
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                    Minimalist design aesthetic
                  </li>
                </ul>
              </div>

              {/* Action Button */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <button 
                  onClick={() => window.location.href = 'mailto:deepaliagrawal224@gmail.com?subject=Weekly Planner Pre-order Inquiry'}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 md:px-8 py-3 border-2 border-black text-black rounded-full hover:bg-black hover:text-white transition-colors duration-300 text-base md:text-lg"
                >
                  Pre-order Now
                </button>
              </div>
            </div>
          </div>

          {/* Information Cards - Full Width Section */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
              {/* Shipping Info Card */}
              <div className="bg-gradient-to-br from-[#f8f8f8] via-[#f0f0f0] to-[#e8f0f0] rounded-[24px] md:rounded-[32px] p-6 md:p-8">
                <h3 className="text-xl font-fraunces mb-4 text-center md:text-left">Shipping Information</h3>
                <ul className="space-y-2 text-gray-700">
                  {[
                    'Free shipping across India',
                    'Delivery within 5-7 business days',
                    'Secure packaging',
                    'Track your order via email'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Card */}
              <div className="bg-gradient-to-br from-[#f8f8f8] via-[#f0f0f0] to-[#e8f0f0] rounded-[24px] md:rounded-[32px] p-6 md:p-8">
                <h3 className="text-xl font-fraunces mb-4 text-center md:text-left">Have Questions?</h3>
                <p className="text-gray-700 mb-4 text-center md:text-left">
                  Feel free to reach out if you have any questions about the calendar or shipping.
                </p>
                <div className="text-center md:text-left">
                  <a 
                    href="mailto:deepaliagrawal224@gmail.com"
                    className="inline-flex items-center justify-center px-6 py-2 border border-black rounded-full hover:bg-black hover:text-white transition-colors duration-300"
                  >
                    deepaliagrawal224@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PurchaseFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        price={discountedPrice}
      />
    </>
  );
}

export default Shop;